// Canvas API client — Model Route integration surface.
// Not implemented (no UI need yet): PATCH /resources/route-{id}/security (credential
// mode switch — routes are apikey by default) and the revoke UI.

import canvasHttp from '@/utils/canvasHttp'

import {
  AIGWRouteResource,
  AIGWRouteSecurityResponse,
  CreateRouteRequest,
  CreateRouteResponse,
  ListBillingRecordsParams,
  ListModelsParams,
  ListProjectBillingRecordsParams,
  ListRoutesParams,
  ModelCatalogListResponse,
  ModelVendor,
  MonitorRange,
  PatchRouteBindingRequest,
  PatchRouteMetadataRequest,
  PatchRouteStatusRequest,
  ProjectModelBillingSummaryResponse,
  ProjectResources,
  ProjectRouteSummary,
  RouteAnalyticsSummary,
  RouteBillingRecordsResponse,
  RouteMonitorMetricsResponse,
  RouteMonitorSummary,
} from './types'

// Global Model Route pages always target the user's virtual default Model
// Gateway project; `type=2` must be explicit (OpenAPI §3.2).
const DEFAULT_PID = 'proj-default'
const MODEL_GATEWAY_TYPE = 2

function routePath(routeId: string, suffix = ''): string {
  // route_id from the API is already fully prefixed (route-*); never re-prefix.
  return `/api/v1/${DEFAULT_PID}/resources/${routeId}${suffix}`
}

export const CanvasAPI = {
  /** Route list of the default Model Gateway project (paginated, optional status filter). */
  listRoutes(params: ListRoutesParams = {}): Promise<ProjectResources> {
    return canvasHttp.get(`/api/v1/${DEFAULT_PID}/resources`, {
      params: { type: MODEL_GATEWAY_TYPE, ...params },
    })
  },

  /** Single route detail (full resource including bindings). */
  getRouteDetail(routeId: string): Promise<AIGWRouteResource> {
    return canvasHttp.get(`/api/v1/${DEFAULT_PID}/${routeId}`, {
      params: { type: MODEL_GATEWAY_TYPE },
    })
  },

  /**
   * Route KPI summary; total_routes only counts activated routes.
   * from/to must be provided together or omitted together — when omitted the
   * backend defaults to the current UTC day, so callers wanting the user's
   * local "today" must pass both.
   */
  getRouteKpiSummary(params: { from?: string; to?: string } = {}): Promise<ProjectRouteSummary> {
    return canvasHttp.get(`/api/v1/${DEFAULT_PID}/resources/routes/summary`, {
      params: { type: MODEL_GATEWAY_TYPE, ...params },
    })
  },

  /** Activate (1) / suspend (2) a route. */
  patchRouteStatus(routeId: string, body: PatchRouteStatusRequest): Promise<AIGWRouteResource> {
    return canvasHttp.patch(routePath(routeId, '/status'), body, {
      params: { type: MODEL_GATEWAY_TYPE },
    })
  },

  /** Rename / re-describe a route. `name` is required by the API even for description-only edits. */
  patchRouteMetadata(routeId: string, body: PatchRouteMetadataRequest): Promise<AIGWRouteResource> {
    return canvasHttp.patch(routePath(routeId, '/metadata'), body, {
      params: { type: MODEL_GATEWAY_TYPE },
    })
  },

  /** Delete a route with its bindings and client credential; settled billing records are kept. */
  deleteRoute(routeId: string): Promise<void> {
    return canvasHttp
      .delete(routePath(routeId), { params: { type: MODEL_GATEWAY_TYPE } })
      .then(() => undefined)
  },

  /** Settled billing summary; from/to must be provided together or omitted together. */
  getRouteBillingSummary(
    routeId: string,
    params: { from?: string; to?: string } = {},
  ): Promise<RouteAnalyticsSummary> {
    return canvasHttp.get(routePath(routeId, '/billing/summary'), {
      params: { type: MODEL_GATEWAY_TYPE, ...params },
    })
  },

  /** Settled billing windows, newest first. route_type filter is integer 1|2|3 only. */
  listRouteBillingRecords(
    routeId: string,
    params: ListBillingRecordsParams = {},
  ): Promise<RouteBillingRecordsResponse> {
    return canvasHttp.get(routePath(routeId, '/billing/records'), {
      params: { type: MODEL_GATEWAY_TYPE, ...params },
    })
  },

  /** Token totals + top models; from/to must be provided together or omitted together. */
  getRouteMonitorSummary(
    routeId: string,
    params: { from?: string; to?: string } = {},
  ): Promise<RouteMonitorSummary> {
    return canvasHttp.get(routePath(routeId, '/monitor/summary'), {
      params: { type: MODEL_GATEWAY_TYPE, ...params },
    })
  },

  /** Usage time series for charts (no chart UI yet — monitor-detail page pending design). */
  getRouteMonitorMetrics(
    routeId: string,
    range: MonitorRange = 'all',
  ): Promise<RouteMonitorMetricsResponse> {
    return canvasHttp.get(routePath(routeId, '/monitor/metrics'), {
      params: { type: MODEL_GATEWAY_TYPE, range },
    })
  },

  /**
   * Official model catalog. Server-side filters: `q`(name keyword)/ `status` /
   * `vendors`(comma-separated ids)/ `caps`(AND bitmask)+ `page`/`per_page`.
   * No model_type filter — the catalog is MaaS-only for now.
   */
  listModels(params: ListModelsParams = {}): Promise<ModelCatalogListResponse> {
    return canvasHttp.get('/api/v1/models', { params })
  },

  /** Create a route and bind models in one transaction; response carries the one-time api_key. */
  createRoute(body: CreateRouteRequest): Promise<CreateRouteResponse> {
    return canvasHttp.post(`/api/v1/${DEFAULT_PID}/resources/routes`, body, {
      params: { type: MODEL_GATEWAY_TYPE },
    })
  },

  /**
   * Atomic batch binding update — the whole batch is validated first and written in one
   * DB transaction; any failure rolls back everything. status 2 disables a binding.
   */
  patchRouteBindings(
    routeId: string,
    bindings: PatchRouteBindingRequest[],
  ): Promise<AIGWRouteResource> {
    return canvasHttp.patch(
      routePath(routeId),
      { bindings },
      { params: { type: MODEL_GATEWAY_TYPE } },
    )
  },

  getProjectBillingSummary(): Promise<ProjectModelBillingSummaryResponse> {
    return canvasHttp.get(`/api/v1/${DEFAULT_PID}/billing/models`, {
      params: { type: MODEL_GATEWAY_TYPE },
    })
  },

  /** Settled billing records across all routes of the project (records carry route_id/route_name). */
  listProjectBillingRecords(
    params: ListProjectBillingRecordsParams = {},
  ): Promise<RouteBillingRecordsResponse> {
    return canvasHttp.get(`/api/v1/${DEFAULT_PID}/billing/records`, {
      params: { type: MODEL_GATEWAY_TYPE, ...params },
    })
  },

  /** Supported model vendors — render vendor filter options from this, the enum grows. */
  listModelVendors(): Promise<ModelVendor[]> {
    return canvasHttp.get('/api/v1/models/vendors')
  },

  /**
   * Rotate the route's client credential: keeps auth mode + credential ID, returns the new
   * plaintext (api_key / access_key+secret_key) ONCE in this response only.
   */
  rotateRouteCredential(routeId: string): Promise<AIGWRouteSecurityResponse> {
    return canvasHttp.post(routePath(routeId, '/security/rotate'), undefined, {
      params: { type: MODEL_GATEWAY_TYPE },
    })
  },

  /**
   * Disable the route's client credential without switching to `unsafe`; idempotent.
   * A revoked credential must be re-created via PATCH /security.
   */
  revokeRouteCredential(routeId: string): Promise<AIGWRouteSecurityResponse> {
    return canvasHttp.post(routePath(routeId, '/security/revoke'), undefined, {
      params: { type: MODEL_GATEWAY_TYPE },
    })
  },
}
